import { colors } from '../constants';

/**
 * An outcome that came out below zero reads red, over whatever colour the row
 * would otherwise use. Spread it into the `sx` of a figure that is a result —
 * a profit, a margin, what an item takes home. A deduction is not a result: a
 * commission or a remitted tax is negative by nature and is coloured where it
 * is written.
 */
export function negativeTone(value) {
  return value < 0 ? { color: colors.destructive } : null;
}

/**
 * Accounting notation: anything below zero reads in parentheses instead of
 * carrying a sign, e.g. "($1,234)" and "(12.50%)".
 *
 * Every formatter below applies it, which is the whole point of having it
 * here — a loss, a negative margin and a deducted amount then read the same
 * on every page, and no screen prepends a minus of its own. Screens that show
 * a deduction (a commission, the remitted sales tax) pass the amount in as
 * negative so it goes through this same path.
 */
function accounting(body, n) {
  return n < 0 ? `(${body})` : body;
}

/** Every amount on screen reads to the cent, e.g. "$1,234.50". */
export function fmt(n) {
  return accounting(
    '$' +
      Math.abs(n).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    n
  );
}

export function fmtD(n, signed = false) {
  const body =
    '$' +
    Math.abs(n).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  if (n < 0) return `(${body})`;
  return signed && n > 0 ? `+${body}` : body;
}

export function fmtN(n) {
  return accounting(Math.round(Math.abs(n)).toLocaleString('en-US'), n);
}

/** Every percentage on screen reads to two decimals, e.g. "30.00%". */
export function pct(n) {
  return accounting(pctVal(Math.abs(n)) + '%', n);
}

/**
 * The same figure without the unit or the parentheses, for the fields the
 * owner types into: those have to stay parseable when they are read back. It
 * is kept free of thousands separators because the field strips them again.
 */
export function pctVal(n) {
  return n.toFixed(2);
}

export function fmtShort(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}

export function fmtFriendly(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(d.getDate()).padStart(2, '0')}`;
}

export function todayISO() {
  return toISODate(new Date());
}

export function rangeDays(a, b) {
  if (!a || !b) return 0;
  const d =
    (new Date(b).getTime() - new Date(a).getTime()) / (1000 * 60 * 60 * 24) + 1;
  return Math.max(1, Math.round(d));
}

export function marginColor(value) {
  return value >= 60 ? '#22c55e' : value >= 40 ? '#f59e0b' : '#ef4444';
}

export const DATE_OPTIONS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'thismonth', label: 'This Month' },
  { id: 'prevmonth', label: 'Last Month' },
  { id: 'custom', label: 'Custom' },
];

export const DATE_LABELS = {
  today: 'Today',
  yesterday: 'Yesterday',
  thismonth: 'This Month',
  prevmonth: 'Last Month',
  month: 'Month',
  custom: 'Custom Range',
};

export function currentMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Human label for a "YYYY-MM" month value, e.g. "March 2026". */
export function monthLabel(month) {
  if (!/^\d{4}-\d{2}$/.test(month)) return 'Month';
  if (month === currentMonthStr()) return 'This month';
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

/** Resolve the display label for the current date selection. */
export function dateRangeLabel(range, month, customLabel) {
  if (range === 'custom') return customLabel;
  if (range === 'month') return monthLabel(month);
  return DATE_LABELS[range];
}
